if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.updateMinSendOnDate();
        if (this.sendonInput) {
          // Cover every interaction vector: native picker, keyboard, paste, autofill
          ['focus', 'click', 'input', 'change', 'keyup', 'paste'].forEach((evt) => {
            this.sendonInput.addEventListener(evt, this.updateMinSendOnDate.bind(this));
          });
          // keydown fires before the value updates — re-check after one tick
          this.sendonInput.addEventListener('keydown', () => {
            setTimeout(this.updateMinSendOnDate.bind(this), 0);
          });
          // iOS's native date picker doesn't grey out dates before `min` the
          // way Android's does, and can commit a value without ever firing
          // `change` on some versions — `blur` is the backstop for that.
          this.sendonInput.addEventListener('blur', this.updateMinSendOnDate.bind(this));

          // The native picker's calendar UI is rendered by the OS, not the
          // page — there is no DOM access to grey out or disable individual
          // days inside it, so `min` alone can't stop past dates being
          // tappable there on iOS. Swap in our own calendar grid on iOS only,
          // where every past day is rendered genuinely disabled. The min/blur
          // correction above stays in place as a safety net underneath it.
          if (this.isIOSDevice()) {
            this.setupIOSSendOnCalendar();
          }
        }

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      updateMinSendOnDate() {
        if (!this.sendonInput) return;
        // Always derive "today" from the user's local timezone, not UTC
        const localToday = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .split('T')[0];
        this.sendonInput.min = localToday;

        // iOS's native date picker (the spinning wheels) doesn't grey out or
        // block dates before `min` the way Android's Material picker does —
        // a long-standing WebKit limitation, not something the `min`
        // attribute alone can fix there. We can't stop a past date being
        // picked in the UI on iOS, so correct it right after: `rangeUnderflow`
        // reflects the browser's own parsed value, which is more reliable
        // across devices than comparing date strings by hand.
        const value = this.sendonInput.value;
        const isPast = value && (value < localToday || this.sendonInput.validity.rangeUnderflow);
        if (isPast) {
          // Snap forward to the earliest allowed date instead of clearing,
          // so it's obvious the selection was corrected rather than lost.
          this.sendonInput.value = localToday;
        }
      }

      isIOSDevice() {
        const ua = navigator.userAgent || navigator.vendor || '';
        const isAppleTouch = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        // iPadOS 13+ reports itself as a Mac, so detect it via touch support.
        const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        return isAppleTouch || isIPadOS;
      }

      parseISODate(iso) {
        const [year, month, day] = iso.split('-').map(Number);
        return new Date(year, month - 1, day);
      }

      toISODate(date) {
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
      }

      startOfMonth(date) {
        return new Date(date.getFullYear(), date.getMonth(), 1);
      }

      setupIOSSendOnCalendar() {
        const input = this.sendonInput;
        const field = input.closest('.field') || input.parentElement;
        if (!field) return;

        // Swap the native date picker trigger for a plain, readonly text
        // field so tapping it opens our calendar instead of iOS's own.
        // The submitted value/format (YYYY-MM-DD) is unchanged.
        input.setAttribute('type', 'text');
        input.setAttribute('readonly', 'readonly');
        input.setAttribute('inputmode', 'none');
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'dialog');
        input.setAttribute('aria-expanded', 'false');

        // Fixed-position + appended to <body> so it can never be clipped by
        // an ancestor's overflow, and so its placement is computed against
        // the actual viewport rather than the (possibly short) field column.
        const popover = document.createElement('div');
        popover.className = 'laib-send-on-calendar';
        popover.setAttribute('role', 'dialog');
        popover.setAttribute('aria-label', 'Choose a date');
        popover.hidden = true;
        document.body.appendChild(popover);

        const todayISO = this.toISODate(new Date());
        let viewDate = this.startOfMonth(new Date());

        const reposition = () => {
          const rect = input.getBoundingClientRect();
          const gap = 8;
          const popoverHeight = popover.offsetHeight;
          const popoverWidth = popover.offsetWidth;
          const spaceBelow = window.innerHeight - rect.bottom;
          const openAbove = spaceBelow < popoverHeight + gap && rect.top > spaceBelow;

          popover.style.top = openAbove
            ? `${Math.max(gap, rect.top - popoverHeight - gap)}px`
            : `${rect.bottom + gap}px`;

          let left = rect.left;
          const maxLeft = window.innerWidth - popoverWidth - gap;
          left = Math.min(Math.max(gap, left), Math.max(gap, maxLeft));
          popover.style.left = `${left}px`;
        };

        const render = () => {
          const year = viewDate.getFullYear();
          const month = viewDate.getMonth();
          const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
          const firstWeekday = new Date(year, month, 1).getDay();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const canGoPrev = viewDate > this.startOfMonth(new Date());

          let cells = '';
          for (let i = 0; i < firstWeekday; i++) {
            cells += '<span class="laib-send-on-calendar__cell" aria-hidden="true"></span>';
          }
          for (let d = 1; d <= daysInMonth; d++) {
            const iso = this.toISODate(new Date(year, month, d));
            // Genuinely non-interactive — `disabled` on a <button>, not just
            // styled to look that way, so it can't be tapped at all.
            const isPast = iso < todayISO;
            const isSelected = input.value === iso;
            cells += `<button type="button" class="laib-send-on-calendar__cell laib-send-on-calendar__day${isPast ? ' is-disabled' : ''}${isSelected ? ' is-selected' : ''}" data-date="${iso}"${isPast ? ' disabled aria-disabled="true"' : ''}>${d}</button>`;
          }

          popover.innerHTML = `
            <div class="laib-send-on-calendar__header">
              <button type="button" class="laib-send-on-calendar__nav" data-nav="prev" aria-label="Previous month"${canGoPrev ? '' : ' disabled aria-disabled="true"'}>&#8249;</button>
              <span class="laib-send-on-calendar__label">${monthLabel}</span>
              <button type="button" class="laib-send-on-calendar__nav" data-nav="next" aria-label="Next month">&#8250;</button>
            </div>
            <div class="laib-send-on-calendar__weekdays">
              ${['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => `<span>${d}</span>`).join('')}
            </div>
            <div class="laib-send-on-calendar__grid">${cells}</div>
          `;
          // Content height just changed (month nav, etc.) — repositioning
          // after render keeps it anchored correctly, including the
          // above/below flip, instead of overflowing off-screen.
          reposition();
        };

        const onViewportChange = () => reposition();

        const open = () => {
          viewDate = this.startOfMonth(input.value ? this.parseISODate(input.value) : new Date());
          popover.hidden = false;
          render();
          input.setAttribute('aria-expanded', 'true');
          window.addEventListener('scroll', onViewportChange, true);
          window.addEventListener('resize', onViewportChange);
        };

        const close = () => {
          popover.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          window.removeEventListener('scroll', onViewportChange, true);
          window.removeEventListener('resize', onViewportChange);
        };

        input.addEventListener('click', () => {
          if (popover.hidden) open();
          else close();
        });

        input.addEventListener('keydown', (evt) => {
          if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();
            if (popover.hidden) open();
          }
        });

        popover.addEventListener('click', (evt) => {
          const nav = evt.target.closest('[data-nav]');
          if (nav) {
            if (nav.disabled) return;
            const dir = nav.dataset.nav === 'next' ? 1 : -1;
            viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + dir, 1);
            render();
            return;
          }

          const day = evt.target.closest('.laib-send-on-calendar__day');
          if (day && !day.disabled) {
            input.value = day.dataset.date;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
            close();
          }
        });

        document.addEventListener('click', (evt) => {
          if (!popover.hidden && !popover.contains(evt.target) && evt.target !== input) close();
        });

        document.addEventListener('keydown', (evt) => {
          if (evt.key === 'Escape' && !popover.hidden) {
            close();
            input.focus();
          }
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
        this.updateMinSendOnDate();
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}
